/* eslint-disable react/react-in-jsx-scope */
import noResultsImg from '@assets/img/no-results-logs.png';
import { ReactComponent as DeleteOutlined } from '@assets/svg/DeleteOutlined.svg';
import { ConfirmationModal, CustomButton, CustomDrawer, OrganizationDetailColumnsSelector, OrganizationDetailSingleSelector, SelectorOptions } from '@components';
import {
  ClientRole,
  InvitationStatus,
  OnboardingStatus,
  OrganizationClientsFilter,
  OrganizationLog,
  Client,
  OrganizationStatus,
} from '@graphql';
import { AnalyticsOutlined, Close, MoreVert } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import StorageOutlined from '@mui/icons-material/StorageOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridCellParams,
  GridColumns,
  GridDensity,
  GridRenderCellParams,
  GridRowId,
  GridToolbarContainer
} from '@mui/x-data-grid';
import { appContext, escapeCsvCell, formatDateAndHour, getCurrentYear, paths } from '@utils';
import { observer } from 'mobx-react';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, SxProps } from '@mui/system';
import { COLORS } from '@styles';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { ClientDrawer } from '../ClientDrawer';
import { ImportClient } from '../ImportClient';
import { filterLogsByAdmin, logAdminName, SYSTEM_ADMINISTRATOR, uniqueLogAdmins } from './orgLogs';



export enum ClientRoleFilter {
  ALL = 'All',
  ADMIN = ClientRole.Admin,
  MANAGER = ClientRole.Manager,
  BASIC = ClientRole.Basic,
}

export enum InvitationStatusFilter {
  ALL = 'All',
  PENDING = InvitationStatus.Pending,
  ACCEPTED = InvitationStatus.Accepted,
  SENT = InvitationStatus.Sent,
}

export enum OnboardingStatusFilter {
  ALL = 'All',
  PENDING = OnboardingStatus.Pending,
  COMPLETED = OnboardingStatus.Completed,
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

export type OrganizationDetailTableColumns = "id" | "email" | "workEmail" | "firstName" | "lastName" | "phone" | "role" | "invitationStatus" | "onboardingStatus"
const DEFAULT_TABLE_COLUMNS: OrganizationDetailTableColumns[] = ["id", "email", "workEmail", "firstName", "lastName", "phone", "role", "invitationStatus", "onboardingStatus"];

const CLIENT_ROLE_OPTIONS: SelectorOptions<ClientRoleFilter>[] = [
  {
    label: 'All',
    value: ClientRoleFilter.ALL,
  },
  {
    label: 'Admin',
    value: ClientRoleFilter.ADMIN,
  },
  {
    label: 'Manager',
    value: ClientRoleFilter.MANAGER,
  },
  {
    label: 'Basic',
    value: ClientRoleFilter.BASIC,
  },
]

const INVITATION_STATUS_OPTIONS: SelectorOptions<InvitationStatusFilter>[] = [
  {
    label: 'All',
    value: InvitationStatusFilter.ALL,
  },
  {
    label: 'Pending',
    value: InvitationStatusFilter.PENDING,
  },
  {
    label: 'Accepted',
    value: InvitationStatusFilter.ACCEPTED,
  },
  {
    label: 'Sent',
    value: InvitationStatusFilter.SENT,
  },
]

const ONBOARDING_STATUS_OPTIONS: SelectorOptions<OnboardingStatusFilter>[] = [
  {
    label: 'All',
    value: OnboardingStatusFilter.ALL,
  },
  {
    label: 'Pending',
    value: OnboardingStatusFilter.PENDING,
  },
  {
    label: 'Completed',
    value: OnboardingStatusFilter.COMPLETED,
  },
]

interface TableSettingsFilter {
  columns: OrganizationDetailTableColumns[];
  tableDensity: GridDensity;
}

export interface OrgDetailsFilterProps {
  userRole: ClientRoleFilter;
  invitationStatus: InvitationStatusFilter;
  onboardingStatus: OnboardingStatusFilter;
  tableSettings: TableSettingsFilter;
}

/** Table filters as sent to the paginated clients query ("All" means no filter). */
const toClientsFilter = (
  { userRole, invitationStatus, onboardingStatus }: OrgDetailsFilterProps,
  search: string,
): OrganizationClientsFilter => ({
  search: search.trim() || null,
  role: userRole !== ClientRoleFilter.ALL ? (userRole as unknown as ClientRole) : null,
  invitationStatus:
    invitationStatus !== InvitationStatusFilter.ALL
      ? (invitationStatus as unknown as InvitationStatus)
      : null,
  onboardingStatus:
    onboardingStatus !== OnboardingStatusFilter.ALL
      ? (onboardingStatus as unknown as OnboardingStatus)
      : null,
});

interface OrganizationDetailCustomToolbarProps {
  isRowsSelected: boolean;
  searchQuery: string;
  handleInviteClient: () => void;
  handleDeleteMultipleModal: () => void;
  handleExportCsv: () => void;
  isExporting: boolean;
  setSearchQuery: (value: string) => void;
  filter: OrgDetailsFilterProps;
  setFilter: Dispatch<SetStateAction<OrgDetailsFilterProps>>;
  isInviteMultipleDisabled: boolean;
}

interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  const CHIP_STYLE: SxProps = { backgroundColor: COLORS.HELP_NEUTRAL, color: COLORS.NEUTRAL_WHITE, borderRadius: '0.5rem' };

  return <Chip label={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>{label} <Close sx={{ cursor: 'pointer' }} onClick={onRemove} /> </Typography>} sx={CHIP_STYLE} />;
}


function CustomToolbar({
  isRowsSelected,
  handleInviteClient,
  handleDeleteMultipleModal,
  handleExportCsv,
  isExporting,
  setSearchQuery,
  searchQuery,
  filter,
  setFilter,
  isInviteMultipleDisabled,
}: OrganizationDetailCustomToolbarProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [isImportClientOpen, setImportClientOpen] = useState<boolean>(false);
  const handleClose = () => {
    setOpen(!open);
  };

  const handleClientImportOpen = () => {
    setImportClientOpen(!isImportClientOpen);
  };

  const hasFilters = filter.invitationStatus !== InvitationStatusFilter.ALL ||
    filter.onboardingStatus !== OnboardingStatusFilter.ALL ||
    filter.userRole !== ClientRoleFilter.ALL

  const onResetFilter = (filterKey: keyof OrgDetailsFilterProps, allValue: any) => () => {
    setFilter(prev => ({ ...prev, [filterKey]: allValue }));
  }

  const onClearAllFilters = () => {
    setFilter(prev => ({ ...prev, invitationStatus: InvitationStatusFilter.ALL, onboardingStatus: OnboardingStatusFilter.ALL, userRole: ClientRoleFilter.ALL }));
  }


  return (
    <>
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <Box display="flex" flex={1} flexDirection="column" gap="0.25rem">
          <Box display="flex" flex={1} justifyContent="space-between" gap="0.25rem">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap="0.5rem">
                <OrganizationDetailSingleSelector<ClientRoleFilter> value={filter.userRole} setValue={(value: ClientRoleFilter) => setFilter(prev => ({ ...prev, userRole: value }))} options={CLIENT_ROLE_OPTIONS} label="User Role" />

                <OrganizationDetailSingleSelector<InvitationStatusFilter> value={filter.invitationStatus} setValue={(value: InvitationStatusFilter) => setFilter(prev => ({ ...prev, invitationStatus: value }))} options={INVITATION_STATUS_OPTIONS} label="Invitation Staus" />

                <OrganizationDetailSingleSelector<OnboardingStatusFilter> value={filter.onboardingStatus} setValue={(value: OnboardingStatusFilter) => setFilter(prev => ({ ...prev, onboardingStatus: value }))} options={ONBOARDING_STATUS_OPTIONS} label="Onboarding Status" />

                <OrganizationDetailColumnsSelector settings={filter.tableSettings} setSettings={(value: OrgDetailsFilterProps["tableSettings"]) => setFilter(prev => ({ ...prev, tableSettings: value }))} />
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                style={{ marginRight: 16, width: `23rem` }}
                InputProps={{
                  endAdornment: <SearchIcon />,
                }}
              />
              <Button variant="contained" onClick={handleClientImportOpen}>
                Import Client
              </Button>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  ml: '1rem',
                }}
              >
                Add Client
              </Button>
              <Button
                variant="contained"
                onClick={handleExportCsv}
                disabled={isExporting}
                sx={{
                  ml: '1rem',
                }}
              >
                {isExporting ? 'Exporting...' : 'Export Users'}
              </Button>
              {isRowsSelected && (
                <>
                  <Tooltip
                    title={isInviteMultipleDisabled
                      ? 'Organization setup is not complete and client is not an admin, or clients are already onboarded'
                      : ''
                    }
                    placement="bottom"
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleInviteClient}
                        sx={{
                          ml: '1rem',
                        }}
                        disabled={isInviteMultipleDisabled}
                      >
                        Invite Clients
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    onClick={() => handleDeleteMultipleModal()}
                    sx={{
                      ml: '1rem',
                    }}
                  >
                    Delete Clients
                  </Button>
                </>
              )}
            </Box>
          </Box>
          {hasFilters && <Box sx={{ marginTop: '0.5rem' }} display='flex' gap='0.5rem' flexWrap='wrap'>
            {filter.userRole !== ClientRoleFilter.ALL && <FilterChip label={filter.userRole} onRemove={onResetFilter('userRole', ClientRoleFilter.ALL)} />}
            {filter.invitationStatus !== InvitationStatusFilter.ALL && <FilterChip label={filter.invitationStatus} onRemove={onResetFilter('invitationStatus', InvitationStatusFilter.ALL)} />}
            {filter.onboardingStatus !== OnboardingStatusFilter.ALL && <FilterChip label={filter.onboardingStatus} onRemove={onResetFilter('onboardingStatus', OnboardingStatusFilter.ALL)} />}

            <Button variant='outlined' sx={{ height: '100%', padding: "0.25rem 1rem", fontSize: '0.75rem', fontWeight: 600, color: COLORS.NEUTRAL_WHITE, borderRadius: "0.25rem", borderColor: COLORS.NEUTRAL_LIGHT_1 }} onClick={onClearAllFilters}>
              Clear All
            </Button>
          </Box>}
        </Box>
      </GridToolbarContainer>
      <ClientDrawer open={open} onClose={handleClose} />
      <ImportClient open={isImportClientOpen} onClose={handleClientImportOpen} />
    </>

  );
}

function LogItem({ log }: { log: OrganizationLog }) {
  // Define inline styles for the log item layout and design
  const styles = {
    main: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      paddingBlock: '1rem',
    } as React.CSSProperties,
    icon: {
      backgroundColor: '#333F66',
      color: '#EEF0F6',
      maxWidth: '32px',
      maxHeight: '32px',
      borderRadius: '50%',
      padding: '6px',
    } as React.CSSProperties,
    infoContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      justifyContent: 'flex-start',
    } as React.CSSProperties,
    adminNameSpan: {
      color: '#5FC6E3',
      fontSize: '12px',
      fontWeight: '600',
    } as React.CSSProperties,
    actionNameSpan: {
      color: '#DDE1EE',
      fontSize: '14px',
      fontWeight: '600',
    } as React.CSSProperties,
    timeSpan: {
      color: '#BCC3DC',
      fontSize: '12px',
    } as React.CSSProperties,
  };

  // JSX for rendering a single log item with an icon, admin name, action, and timestamp
  return (
    <div>
      <Divider />
      <div style={styles.main}>
        <SyncAltIcon style={styles.icon} />
        <div style={styles.infoContainer}>
          <span style={styles.adminNameSpan}>{logAdminName(log)}</span>
          <span style={styles.actionNameSpan} title={log.operationName}>
            {log.actionLabel ?? log.operationName}
          </span>
          <span style={styles.timeSpan}>{formatDateAndHour(log.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

interface LogsModalProps {
  open: boolean; // Indicates if the modal is open or closed
  onClose: () => void; // Callback function to close the modal
  logs: OrganizationLog[]; // List of logs to be displayed in the modal
}

function LogsModal({ open, onClose, logs }: LogsModalProps) {
  const [selected, setSelected] = useState<string[]>(['all']); // Stores the selected admins for filtering
  const [filteredLogs, setFilteredLogs] = useState<OrganizationLog[]>(logs); // Holds the filtered logs based on selection
  const [timePeriod, setTimePeriod] = useState<number>(-1); // Stores the selected time period for filtering

  // Function to filter logs based on the selected time period
  const getLogsBasedOnTime = (logsToFilter: OrganizationLog[], period?: number) => {
    const periodToUse = period ?? timePeriod; // Use the provided period or the current state value
    if (periodToUse === -1) return logsToFilter; // If period is -1, return all logs
    const now = new Date();
    const pastDate = new Date(now);
    pastDate.setDate(now.getDate() - periodToUse); // Calculate the start date for filtering

    // Filter logs within the specified time range
    return logsToFilter.filter((log) => {
      const date = new Date(log.createdAt);
      return date >= pastDate && date <= now;
    });
  };

  // Function to filter logs based on selected administrators
  const getLogsBasedOnAdmin = (logsToFilter: OrganizationLog[], admins?: string[]) => {
    const adminsToUse = admins ?? selected; // Use provided admins or the current selected ones
    return filterLogsByAdmin(logsToFilter, adminsToUse); // 'all' keeps every log; null (System) admins match only 'all'
  };

  // Handle change in selected administrators
  const handleChangeAdm = (event: any) => {
    const value = event?.target?.value;

    if (value.length === 0) return; // If no value is selected, do nothing

    const admins = typeof value === 'string' ? value.split(',') : value; // Split comma-separated string into array
    setSelected(admins);

    // Filter logs by both admin and time
    const filterByAdmins = getLogsBasedOnAdmin(logs, admins);
    const filterByTime = getLogsBasedOnTime(filterByAdmins);
    setFilteredLogs(filterByTime);
  };

  // Handle change in selected time period
  const handleChangeTime = (event: any) => {
    const period = event.target.value;
    setTimePeriod(period); // Update the selected time period
    setFilteredLogs(getLogsBasedOnTime(logs, period)); // Filter logs by the new time period
  };

  // Time period options for filtering
  const timeFilter = [
    { value: 7, label: 'Last 7 Days' },
    { value: 30, label: 'Last 30 Days' },
    { value: 90, label: 'Last 3 months' },
    { value: 180, label: 'Last 6 months' },
    { value: 365, label: `This year (${getCurrentYear()})` },
    { value: 730, label: `Last year (${getCurrentYear(1)})` },
    { value: -1, label: 'All Time' },
  ];

  // Generate a list of unique superadmins for the administrator filter
  const getSuperAdmins = () => uniqueLogAdmins(logs); // Skips System logs (null admin) and duplicates
  const administratorFilter = [
    { value: 'all', label: 'All Administrators' },
    { value: SYSTEM_ADMINISTRATOR, label: 'System' },
  ].concat(getSuperAdmins());

  // Handle the rendering of the selected value in the administrator select
  const handleRenderValue = () => {
    if (selected.length === 1) {
      return administratorFilter.find((item) => item.value === selected[0])?.label;
    }
    return `${selected.length} Administrators`;
  };

  // Format the display of the number of results
  const formatResults = () => {
    if (filteredLogs.length === 0) return 'No results';
    if (filteredLogs.length === 1) return '1 Result';
    return `${filteredLogs.length} Results`;
  };

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      headerLabel="Activity Log"
      headerIcon={<StorageOutlined sx={{ color: '#5FC6E3' }} />}
      PaperProps={{
        sx: {
          backgroundColor: '#000',
          backgroundImage: 'none',
          width: '384px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <section
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            fontFamily: 'Inter',
            marginBottom: '1.5rem',
            padding: '16px 16px 0 16px',
            flexShrink: 0,
          }}
        >
          <FormControl fullWidth >
            <InputLabel style={{ color: 'white', fontWeight: '600' }}>Administrator</InputLabel>
            <Select
              name="role"
              sx={{
                borderRadius: '16px',
                color: '#BCC3DC',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: '#232A43',
                },
              }}
              label="Administrator"
              multiple
              value={selected}
              onChange={handleChangeAdm}
              renderValue={handleRenderValue}
            >
              <MenuItem value="all">
                <Checkbox checked={selected.includes('all')} />
                <ListItemText primary="All Administrators" />
              </MenuItem>
              <MenuItem value={SYSTEM_ADMINISTRATOR}>
                <Checkbox checked={selected.includes(SYSTEM_ADMINISTRATOR)} />
                <ListItemText primary="System" />
              </MenuItem>
              {getSuperAdmins().map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={selected.includes(item.value)} />
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel style={{ color: 'white', fontWeight: '600' }}>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleChangeTime}
              sx={{
                borderRadius: '16px',
                color: '#BCC3DC',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: '#232A43',
                },
              }}
              label="Time Period"
            >
              {timeFilter.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </section>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: '0 16px',
          }}
        >
          <span
            style={{
              color: '#DDE1EE',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '1rem',
              flexShrink: 0,
            }}
          >
            {formatResults()}
          </span>

          {filteredLogs.length ? (
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                paddingRight: '8px',
                minHeight: 0,
                paddingBottom: '24px',
              }}
            >
              {filteredLogs.map((log, index) => (
                <LogItem
                  // OrganizationLog has no id field; index disambiguates same-timestamp System entries
                  // (which otherwise all share the 'system' suffix) and same-admin entries logged in the same millisecond.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${log.createdAt}-${log.superadmin?.id ?? 'system'}-${index}`}
                  log={log}
                />
              ))}
            </div>
          ) : (
            <>
              <Divider orientation="horizontal" flexItem />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '60px',
                  flex: 1,
                }}
              >
                <img src={noResultsImg} alt="" />
              </div>
            </>
          )}
        </div>
      </div>
    </CustomDrawer>
  );
}

export const OrganizationDetailTable = observer(() => {
  // const [openAdvisorModal, setOpenAdvisorModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openDeleteMultipleModal, setOpenDeleteMultipleModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [client, setClient] = useState<GridRowId[] | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [openLogsModal, setOpenLogsModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<OrgDetailsFilterProps>({
    invitationStatus: InvitationStatusFilter.ALL, onboardingStatus: OnboardingStatusFilter.ALL, userRole: ClientRoleFilter.ALL,
    tableSettings: { columns: DEFAULT_TABLE_COLUMNS, tableDensity: "standard" }
  });

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, selectedClientData: Client) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuAnchorEl(event.currentTarget);
    setSelectedClient(selectedClientData);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  // const handleAdvisorModal = useCallback(() => {
  //   setOpenAdvisorModal(!openAdvisorModal);
  // }, [openAdvisorModal]);

  const handleDeleteModal = useCallback(() => {
    setOpenDeleteModal(!openDeleteModal);
    handleCloseMenu();
  }, [openDeleteModal, handleCloseMenu]);

  const handleDeleteMultipleModal = useCallback(() => {
    setOpenDeleteMultipleModal(!openDeleteMultipleModal);
  }, [openDeleteMultipleModal]);

  const handleEditModal = useCallback(() => {
    setOpenEditModal(!openEditModal);
    handleCloseMenu();
  }, [openEditModal, handleCloseMenu]);

  const handleLogsModal = useCallback(() => {
    setOpenLogsModal(!openLogsModal);
  }, [openLogsModal]);

  const {
    organizationStore: {
      deleteClientsFromOrg,
      inviteClient,
      organizationInContext: organization,
      organizationClients,
      organizationClientsTotal,
      organizationClientsResponse,
      organizationClientsLoadedQuery,
      fetchOrganization,
      fetchOrganizationClients,
      fetchAllOrganizationClients,
      refreshOrganizationDetail,
      resetOrganizationInContext,
    },
    historyStore: { history },
  } = useContext(appContext);
  const { id } = useParams<any>();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  // Every client seen on any page, so the roles of rows selected on other pages stay known.
  const knownClientsRef = useRef(new Map<string, Client>());

  // OrganizationDetailPage remounts this component per organization id, so page, filters, search
  // and selection never carry over from another organization.
  useEffect(() => {
    fetchOrganization(id);
  }, [fetchOrganization, id]);

  useEffect(() => resetOrganizationInContext, [resetOrganizationInContext]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const clientsFilter = useMemo(
    () => toClientsFilter(filter, debouncedSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter.userRole, filter.invitationStatus, filter.onboardingStatus, debouncedSearch],
  );

  const filterKey = JSON.stringify(clientsFilter);
  const [page, setPage] = useState<number>(0);
  const [pageQuery, setPageQuery] = useState({ filterKey, pageSize });

  // A new filter or page size starts again from the first page. The state is adjusted during render,
  // so the effects below never request the old page with the new query. A new filter also clears
  // the selection, so bulk actions never reach clients the filter hides.
  if (pageQuery.filterKey !== filterKey || pageQuery.pageSize !== pageSize) {
    if (pageQuery.filterKey !== filterKey) setRowSelectionModel([]);
    setPageQuery({ filterKey, pageSize });
    setPage(0);
  }

  useEffect(() => {
    fetchOrganizationClients(id, { offset: page * pageSize, limit: pageSize, filter: clientsFilter });
  }, [fetchOrganizationClients, id, page, pageSize, clientsFilter]);

  useEffect(() => {
    organizationClients.forEach((clientData) => knownClientsRef.current.set(clientData.id, clientData));
  }, [organizationClients]);

  // After clients were removed the current page can come back empty past the end: go to the last
  // page. Only the response for the page currently shown counts.
  useEffect(() => {
    const loaded = organizationClientsLoadedQuery;
    if (!loaded || page === 0 || organizationClients.length > 0) return;

    const isCurrentPage =
      loaded.offset === page * pageSize &&
      loaded.limit === pageSize &&
      JSON.stringify(loaded.filter) === filterKey;
    if (isCurrentPage) {
      setPage(Math.max(Math.ceil(organizationClientsTotal / pageSize) - 1, 0));
    }
  }, [organizationClientsLoadedQuery, organizationClients, organizationClientsTotal, page, pageSize, filterKey]);

  // For single lines selection, e.g. Three Dots Menus
  // Check if the client is invited and onboarded, or the organization is inactive
  const isInviteSingleDisabled = useMemo(() => {
    const isOnboardCompleted = selectedClient?.onboardingStatus === OnboardingStatus.Completed;
    const isAdmin = selectedClient?.role === ClientRole.Admin;
    const isOrgStatusCreated = organization?.status === OrganizationStatus.Created;
    const isOrgStatusInactive = organization?.status === OrganizationStatus.Inactive;
    return isOnboardCompleted || (!isAdmin && isOrgStatusCreated) || isOrgStatusInactive;

  }, [selectedClient?.onboardingStatus, selectedClient?.role, organization?.status]);

  // For multiple lines selection, e.g. Invite Multiple Clients
  // Check if the clients are invited and onboarded, or the organization is active
  const isInviteMultipleClientsAllowed = useCallback((selectedIds: string[]) => {
    const organizationStatus = organization?.status;

    if (organizationStatus === OrganizationStatus.Active) {
      return true;
    }

    if (organizationStatus === OrganizationStatus.Created) {
      return selectedIds.some(
        (clientId) => knownClientsRef.current.get(clientId)?.role === ClientRole.Admin,
      );
    }

    return false;
  }, [organization?.status]);

  // For multiple lines selection, e.g. Invite Multiple Clients
  // Check if the clients are invited and onboarded, or the organization is not active
  const isInviteMultipleDisabled = useMemo(
    () => !isInviteMultipleClientsAllowed(rowSelectionModel as string[]),
    [rowSelectionModel, isInviteMultipleClientsAllowed],
  );

  const handleInviteClients = useCallback(
    async (selectedClients: string[]) => {
      if (!selectedClients || selectedClients.length === 0) {
        return;
      }

      // If the organization is not active, do not invite clients
      if (!isInviteMultipleClientsAllowed(selectedClients)) {
        toast('Organization setup is not complete and client is not an admin, or clients are already onboarded', { type: 'error' });
        return;
      }

      const result = await inviteClient({
        orgId: id,
        clientIds: selectedClients as string[],
      });

      if (result) {
        await refreshOrganizationDetail(id);
        handleCloseMenu();
        setRowSelectionModel([]);
      }
    },
    [inviteClient, refreshOrganizationDetail, id, isInviteMultipleClientsAllowed, handleCloseMenu, setRowSelectionModel],
  );

  const handleDeleteClients = useCallback(
    async (selectedClients: any) => {
      if (!selectedClients) {
        return;
      }

      const result = await deleteClientsFromOrg({
        orgId: id,
        clients: selectedClients as string[],
      });
      if (result) {
        const deletedIds = new Set<GridRowId>(selectedClients);
        setRowSelectionModel((selected) => selected.filter((rowId) => !deletedIds.has(rowId)));
        await refreshOrganizationDetail(id);
        if (openDeleteMultipleModal) {
          handleDeleteMultipleModal();
          return;
        }
        handleDeleteModal();
      }
    },
    [
      deleteClientsFromOrg,
      refreshOrganizationDetail,
      handleDeleteModal,
      handleDeleteMultipleModal,
      id,
      openDeleteMultipleModal,
    ],
  );

  const columns = useMemo<GridColumns>(
    () => [
      { field: 'id', headerName: 'ID', disableColumnMenu: true, flex: 1 },
      { field: 'email', headerName: 'Email', disableColumnMenu: true, flex: 1 },
      { field: 'workEmail', headerName: 'Work Email', disableColumnMenu: true, flex: 1 },
      { field: 'firstName', headerName: 'First Name', disableColumnMenu: true, flex: 1 },
      { field: 'lastName', headerName: 'Last Name', disableColumnMenu: true, flex: 1 },
      { field: 'phone', headerName: 'Phone', disableColumnMenu: true, flex: 1 },
      { field: 'role', headerName: 'Role', disableColumnMenu: true, flex: 1 },
      {
        field: 'invitationStatus',
        headerName: 'Invitation Status',
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: 'onboardingStatus',
        headerName: 'Onboarding Status',
        disableColumnMenu: true,
        flex: 1,
      },
      // {
      //   field: 'advisor',
      //   headerName: 'Advisor',
      //   disableColumnMenu: true,
      //   flex: 1,
      //   valueGetter: (params: any) => {
      //     if (params.row.advisor)
      //       return `${params.row.advisor.firstName} ${params.row.advisor.lastName}`;
      //     return null;
      //   },
      // },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 60,
        renderCell: (params: GridRenderCellParams<Client>) => (
          <IconButton onClick={(e) => handleOpenMenu(e, params.row)} size="small">
            <MoreVert />
          </IconButton>
        ),
      },
    ],
    [
      // handleAdvisorModal,
      handleOpenMenu,
    ],
  );

  const manageOrgUrl = `${process.env.REACT_APP_CLIENT_URL}/superadmin/organization?token=${organization?.manageOrgToken}`;
  const manageOrgAnalUrl = `${process.env.REACT_APP_CLIENT_URL}/superadmin/organization-analytics?token=${organization?.manageOrgToken}`;
  const canManageOrg = !!organization?.manageOrgToken;
  const manageBillingUrl = `${paths.organizations}/billing/${id}`;

  const deleteClientTitle = useMemo(
    () =>
      openDeleteMultipleModal
        ? `Are you sure you want to delete ${rowSelectionModel?.length} clients from ElektraFi platform?`
        : `Are you sure you want to delete ${clientName} from ElektraFi platform?`,
    [openDeleteMultipleModal, rowSelectionModel, clientName],
  );

  const confirmationText = useMemo(
    () =>
      openDeleteMultipleModal
        ? organization?.name
        : clientName,
    [clientName, organization?.name, openDeleteMultipleModal],
  );

  // Exports every client of the organization (not only the current page or filter), fetched on demand.
  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    const allClients = (await fetchAllOrganizationClients(id)) as Client[] | null;
    setIsExporting(false);
    if (!allClients) return;

    const rows = allClients.map((clientData: Client) => ({
      ID: escapeCsvCell(clientData.id),
      Email: escapeCsvCell(clientData.email),
      'Work Email': escapeCsvCell(clientData.workEmail),
      'First Name': escapeCsvCell(clientData.firstName),
      'Last Name': escapeCsvCell(clientData.lastName),
      Phone: escapeCsvCell(clientData.phone),
      Role: escapeCsvCell(clientData.role),
      'Invitation Status': escapeCsvCell(clientData.invitationStatus),
      'Onboarding Status': escapeCsvCell(clientData.onboardingStatus),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    const today = new Date();
    const exportDate = [today.getFullYear(), today.getMonth() + 1, today.getDate()]
      .map((value) => String(value).padStart(2, '0'))
      .join('');

    saveAs(blob, `users-${organization?.name}-${exportDate}.csv`);
  }, [fetchAllOrganizationClients, id, organization?.name]);

  return (
    <div style={{ height: 'inherit', width: '100%' }}>
      <div style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
        <h2 style={{ color: 'rgb(224, 224, 224)' }}>{organization?.name}</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button
            variant="outlined"
            sx={{ height: 'fit-content', color: 'white', borderColor: '#333F66' }}
            onClick={handleLogsModal}
          >
            <StorageOutlined sx={{ marginRight: '8px', color: '#5FC6E3' }} />
            ACTIVITY LOG
          </Button>
          <CustomButton
            variant="default"
            icon={<AttachMoneyIcon />}
            width="fit-content"
            onClick={() => {
              history.push(manageBillingUrl);
            }}
          >
            Manage Billing
          </CustomButton>
          {canManageOrg && (
            <CustomButton
              variant="default"
              icon={<WorkOutlineIcon />}
              width="fit-content"
              onClick={() => {
                window.open(manageOrgUrl, '_blank');
              }}
            >
              Manage Organization
            </CustomButton>
          )}
          {canManageOrg && (
            <CustomButton
              variant="default"
              icon={<AnalyticsOutlined />}
              width="fit-content"
              onClick={() => {
                window.open(manageOrgAnalUrl, '_blank');
              }}
            >
              Employer Metrics
            </CustomButton>
          )}
        </div>
      </div>
      <DataGrid
        rows={organizationClients}
        rowCount={organizationClientsTotal}
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        keepNonExistentRowsSelected
        selectionModel={rowSelectionModel}
        columns={columns.filter(({ field }) => {
          if (!DEFAULT_TABLE_COLUMNS.includes(field as OrganizationDetailTableColumns)) return true;
          return filter.tableSettings.columns.includes(field as OrganizationDetailTableColumns);
        })}
        checkboxSelection
        density={filter.tableSettings.tableDensity}
        onSelectionModelChange={(items) => setRowSelectionModel(items)}
        componentsProps={{
          toolbar: {
            isRowsSelected: rowSelectionModel.length > 0,
            handleInviteClient: () => handleInviteClients(rowSelectionModel as string[]),
            handleDeleteMultipleModal,
            handleExportCsv,
            isExporting,
            setSearchQuery,
            searchQuery,
            filter,
            setFilter,
            isInviteMultipleDisabled,
          },
        }}
        loading={organizationClientsResponse.status === 'pending'}
        components={{
          Toolbar: CustomToolbar,
        }}
        onCellClick={(params: GridCellParams) => {
          if (params?.field === 'id') {
            toast('Client ID was copied!', { type: 'success' });

            return navigator.clipboard.writeText(params.row.id);
          }

          return null;
        }}
      />
      {/* {openAdvisorModal && (
        <AssignAdvisor handleClose={handleAdvisorModal} client={client as any} />
      )} */}
      <ConfirmationModal
        icon={<DeleteOutlined />}
        title={deleteClientTitle}
        onClose={openDeleteMultipleModal ? handleDeleteMultipleModal : handleDeleteModal}
        isOpen={openDeleteModal || openDeleteMultipleModal}
        variant="destructive"
        confirmationText={confirmationText || ''}
        onConfirm={() => handleDeleteClients(openDeleteMultipleModal ? rowSelectionModel : client)}
        confirmationButtonLabel={`Delete Client${openDeleteMultipleModal ? 's' : ''}`}
      >
        <p>
          Deleting these clients {` `}
          <strong>
            will permanently remove their access ElektraFi and delete their financial data stored on
            the platform.
          </strong>
        </p>
        <p>{`To proceed with the deletion, please type the ${rowSelectionModel?.length > 1 ? 'organization name' : 'client name'
          }:`}</p>
        <strong>{confirmationText}</strong>
      </ConfirmationModal>
      <ClientDrawer open={openEditModal} onClose={handleEditModal} client={selectedClient} isEdit />
      {organization?.orgLogs !== undefined && (
        <LogsModal
          open={openLogsModal}
          onClose={handleLogsModal}
          logs={organization?.orgLogs ?? []}
        />
      )}
      <Menu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Tooltip
          title={isInviteSingleDisabled
            ? 'Organization setup is not complete and client is not an admin, or clients are already onboarded'
            : ''
          }
          placement="left"
        >
          <span>
            <MenuItem
              disabled={isInviteSingleDisabled}
              onClick={() => handleInviteClients([selectedClient?.id as string])}
            >
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText>
                {selectedClient?.invitationStatus === InvitationStatus.Sent ? 'Invite Again' : 'Send Invite'}
              </ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem
          onClick={() => {
            if (selectedClient) {
              setClient([selectedClient.id]);
              // Trim the first and last name to remove any leading or trailing spaces
              const trimmedFirstName = selectedClient.firstName?.trim();
              const trimmedLastName = selectedClient.lastName?.trim();
              const name = `${trimmedFirstName} ${trimmedLastName}`;
              setClientName(name);
              handleDeleteModal();
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete Client</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleEditModal}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>Edit Client</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
});
